import {
  email,
  required,
  useRecordContext,
  useTranslate,
  useUpdate,
  useNotify,
} from "ra-core";
import type { FocusEvent, ClipboardEventHandler } from "react";
import { useFormContext } from "react-hook-form";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";

import { StatusSelector } from "../notes";
import type { Sale, Contact } from "../types";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";

/**
 * The contact form keeps only what a sales manager fills in (owner's rule
 * 04.11): name, phone, email, company, position, account manager, comment.
 * LinkedIn, gender, newsletter, avatar and the Work/Home/Other pickers are
 * gone; their columns stay in the database.
 */
export const ContactInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex gap-6 md:gap-6 flex-col md:flex-row p-1">
      <div className="flex flex-col gap-4 flex-1">
        <TextInput
          source="first_name"
          validate={required()}
          helperText={false}
        />
        <TextInput
          source="last_name"
          validate={required()}
          helperText={false}
        />
        <ContactPhoneAndEmailInputs />
      </div>
      {isMobile ? null : (
        <Separator orientation="vertical" className="flex-shrink-0" />
      )}
      <div className="flex flex-col gap-4 flex-1">
        <ReferenceInput source="company_id" reference="companies" perPage={10}>
          <AutocompleteCompanyInput label="resources.contacts.fields.company_id" />
        </ReferenceInput>
        <TextInput source="title" helperText={false} />
        <ReferenceInput
          reference="sales"
          source="sales_id"
          sort={{ field: "last_name", order: "ASC" }}
          filter={{
            "disabled@neq": true,
          }}
        >
          <SelectInput
            helperText={false}
            optionText={saleOptionRenderer}
            validate={required()}
          />
        </ReferenceInput>
        <TextInput source="background" multiline helperText={false} />
      </div>
    </div>
  );
};

const ContactPhoneAndEmailInputs = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();

  // set first and last name based on email
  const handleEmailChange = (email: string) => {
    const { first_name, last_name } = getValues();
    if (first_name || last_name || !email) return;
    const [first, last] = email.split("@")[0].split(".");
    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));
    setValue(
      "last_name",
      last ? last.charAt(0).toUpperCase() + last.slice(1) : "",
    );
  };

  const handleEmailPaste: ClipboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (e) => {
    const email = e.clipboardData?.getData("text/plain");
    handleEmailChange(email);
  };

  const handleEmailBlur = (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const email = e.target.value;
    handleEmailChange(email);
  };

  return (
    <>
      <ArrayInput source="phone_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="number"
            className="w-full"
            helperText={false}
            label={false}
            type="tel"
            placeholder={translate("resources.contacts.fields.phone_number")}
          />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="email_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="email"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.email")}
            validate={email()}
            onPaste={handleEmailPaste}
            onBlur={handleEmailBlur}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

export const ContactStatusSelector = () => {
  const record = useRecordContext<Contact>();
  const [update] = useUpdate<Contact>();
  const notify = useNotify();
  if (!record) return null;

  const handleStatusChange = (nextStatus: string) => {
    if (nextStatus === record?.status) return;

    update(
      "contacts",
      {
        id: record.id,
        data: { status: nextStatus },
        previousData: record,
      },
      {
        mutationMode: "optimistic",
        onError: (error) => {
          notify(
            typeof error === "string"
              ? error
              : error?.message || "ra.notification.http_error",
            {
              type: "error",
              messageArgs: {
                _: typeof error === "string" ? error : error?.message,
              },
            },
          );
        },
      },
    );
  };

  return (
    <div className="[&_button]:w-auto">
      <StatusSelector
        status={record?.status}
        setStatus={handleStatusChange}
        triggerClassName="w-full"
      />
    </div>
  );
};
